import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import {
  DateInput,
  DocumentTextIcon,
  EyeIcon,
  GeneralList,
  GeneralListSortableTh,
  PencilIcon,
  PlusIcon,
  Select,
  TrashIcon,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../../components/ui';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import { deleteNoonReport } from '../../../services/mutations';
import { NOON_ACTIVITY_LABELS } from './constants';
import { moduleConfig } from './config';
import type { NoonReport } from './types';
import { useNoonReports } from './useData';

function formatActivities(item: NoonReport) {
  if (!item.activities?.length) return '—';
  const labels = item.activities
    .slice(0, 2)
    .map((a) => NOON_ACTIVITY_LABELS[a as keyof typeof NOON_ACTIVITY_LABELS] || a);
  const suffix = item.activities.length > 2 ? ` +${item.activities.length - 2}` : '';
  return `${labels.join(', ')}${suffix}`;
}

const List: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnState = { from: `${location.pathname}${location.search}` };
  const { colors } = useTheme();
  const { can } = useAuth();
  const confirm = useConfirm();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [vesselId, setVesselId] = useState('');
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'reportDate', direction: 'desc' },
    { defaultDirectionForKey: () => 'desc' },
  );
  const { data, total, isLoading, isError, mutate } = useNoonReports(search, page, itemsPerPage, {
    sortKey: sortConfig.key,
    sortDirection: sortConfig.direction,
  }, {
    vesselId,
    reportDateFrom,
    reportDateTo,
  });

  type VesselOptionRow = { id: number; vesselName: string; isActive?: boolean };
  const { data: vesselsRaw, isLoading: vesselsLoading } = useSWR<
    VesselOptionRow[] | { data?: VesselOptionRow[] }
  >('/vessels?page=1&limit=500', fetcher, { revalidateOnFocus: false });
  const vesselOptions = useMemo(() => {
    const list = Array.isArray(vesselsRaw) ? vesselsRaw : vesselsRaw?.data ?? [];
    return list
      .filter((v) => v.isActive !== false)
      .map((v) => ({ value: String(v.id), label: v.vesselName }));
  }, [vesselsRaw]);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [
    search,
    vesselId,
    reportDateFrom,
    reportDateTo,
    itemsPerPage,
    sortConfig.key,
    sortConfig.direction,
  ]);

  const permissionKey = moduleConfig.module.permissionModuleKey;
  const allowEdit = can(permissionKey, 'edit');
  const allowDelete = can(permissionKey, 'delete');
  const totalPages = Math.max(1, Math.ceil((total || 0) / itemsPerPage) || 1);
  const hasFilters = Boolean(search.trim() || vesselId || reportDateFrom || reportDateTo);

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setVesselId('');
    setReportDateFrom('');
    setReportDateTo('');
  };

  const handleDelete = async (e: React.MouseEvent, row: NoonReport) => {
    e.stopPropagation();
    const ok = await confirm({
      title: moduleConfig.delete.title,
      message: moduleConfig.delete.message,
      confirmText: moduleConfig.delete.confirmText,
      cancelText: moduleConfig.delete.cancelText,
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteNoonReport(row.id);
      await mutate();
    } catch {
      window.alert(moduleConfig.delete.errorMessage);
    }
  };

  return (
    <GeneralList
      sorting={{ sortConfig, onSort: handleSort }}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: moduleConfig.search.placeholder,
        title: 'Searches vessel, port, cargo, and notes',
      }}
      actions={
        allowEdit ? (
          <Link
            to={moduleConfig.module.route.new}
            state={returnState}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white shadow-sm',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            New report
          </Link>
        ) : null
      }
      filters={
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-[150px]">
            <Select
              value={vesselId}
              options={vesselOptions}
              placeholder={vesselsLoading ? 'Loading vessels…' : 'Vessel'}
              showClearButton
              searchPlaceholder="Search vessels…"
              onChange={(e) => setVesselId(String(e.target.value))}
            />
          </div>
          <div className="w-[150px]">
            <DateInput
              value={reportDateFrom}
              onChange={(e) => setReportDateFrom(e.target.value)}
              placeholder="Report date from"
              showClearButton
              showQuickFill={false}
              showFormatHint={false}
              maxDate={reportDateTo || undefined}
            />
          </div>
          <div className="w-[150px]">
            <DateInput
              value={reportDateTo}
              onChange={(e) => setReportDateTo(e.target.value)}
              placeholder="Report date to"
              showClearButton
              showQuickFill={false}
              showFormatHint={false}
              minDate={reportDateFrom || undefined}
            />
          </div>
        </div>
      }
      filtersInline
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={isLoading}
      loadingLabel="Loading noon reports…"
      isEmpty={!isError && data.length === 0}
      emptyState={{
        icon: <DocumentTextIcon className="h-12 w-12" />,
        title: hasFilters ? moduleConfig.emptyStates.noFilterResults : moduleConfig.emptyStates.noData,
        hint: hasFilters
          ? 'Try adjusting the filters or clear them to see all reports.'
          : 'Create the first noon report for a vessel.',
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : allowEdit ? (
          <Link
            to={moduleConfig.module.route.new}
            state={returnState}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            New report
          </Link>
        ) : undefined,
      }}
      banner={
        isError ? (
          <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
            Could not load noon reports.
          </p>
        ) : null
      }
      pagination={{
        page,
        totalPages,
        total,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-max border-separate border-spacing-0 text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th
              className={cn(
                'sticky left-0 z-20 whitespace-nowrap px-3 py-3 font-medium',
                colors.surface,
              )}
              style={{ minWidth: '5.5rem' }}
            />
            <GeneralListSortableTh sortKey="reportDate" className="whitespace-nowrap" style={{ minWidth: '9rem' }}>
              Date
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="vesselId" className="whitespace-nowrap" style={{ minWidth: '12rem' }}>
              Vessel
            </GeneralListSortableTh>
            <th className="whitespace-nowrap px-3 py-3 font-medium" style={{ minWidth: '6rem' }}>Time</th>
            <GeneralListSortableTh sortKey="condition" className="whitespace-nowrap" style={{ minWidth: '7rem' }}>
              Condition
            </GeneralListSortableTh>
            <th className="whitespace-nowrap px-3 py-3 font-medium" style={{ minWidth: '13rem' }}>Activities</th>
            <GeneralListSortableTh sortKey="nextPort" className="whitespace-nowrap" style={{ minWidth: '11rem' }}>
              Next port
            </GeneralListSortableTh>
            <th className="whitespace-nowrap px-3 py-3 text-right font-medium" style={{ minWidth: '9rem' }}>
              Covered (NM)
            </th>
            <th className="whitespace-nowrap px-3 py-3 text-right font-medium" style={{ minWidth: '9rem' }}>
              To go (NM)
            </th>
            <th className="whitespace-nowrap px-3 py-3 font-medium" style={{ minWidth: '11rem' }}>Cargo type</th>
            <th className="whitespace-nowrap px-3 py-3 text-right font-medium" style={{ minWidth: '9rem' }}>
              Cargo (MT)
            </th>
            <th className="whitespace-nowrap px-3 py-3 text-right font-medium" style={{ minWidth: '9rem' }}>
              Avg speed
            </th>
            <th className="whitespace-nowrap px-3 py-3 text-right font-medium" style={{ minWidth: '8rem' }}>
              FO ROB
            </th>
            <th className="whitespace-nowrap px-3 py-3 text-right font-medium" style={{ minWidth: '10rem' }}>
              MGO/MDO ROB
            </th>
            <th className="whitespace-nowrap px-3 py-3 font-medium" style={{ minWidth: '17rem' }}>Position</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr
              key={row.id}
              onClick={() => navigate(moduleConfig.module.route.detail(row.id), { state: returnState })}
              className={cn(
                'group cursor-pointer border-b transition last:border-b-0 hover:bg-slate-50/80 dark:hover:bg-slate-800/40',
                colors.border,
              )}
            >
              <td
                className={cn(
                  'sticky left-0 z-10 whitespace-nowrap px-3 py-3.5',
                  colors.surface,
                  'group-hover:bg-slate-50 dark:group-hover:bg-slate-800',
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-nowrap items-center">
                  <Link
                    onClick={(e) => e.stopPropagation()}
                    to={moduleConfig.module.route.detail(row.id)}
                    state={returnState}
                    title="View"
                    aria-label="View report"
                    className="rounded-lg p-1 text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </Link>
                  {allowEdit ? (
                    <Link
                      onClick={(e) => e.stopPropagation()}
                      to={moduleConfig.module.route.edit(row.id)}
                      state={returnState}
                      title="Edit"
                      aria-label="Edit report"
                      className="rounded-lg p-1 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-900"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </Link>
                  ) : null}
                  {allowDelete ? (
                    <button
                      type="button"
                      title="Delete"
                      aria-label="Delete report"
                      onClick={(e) => void handleDelete(e, row)}
                      className="rounded-lg p-1 text-red-600 transition-colors hover:bg-red-50 hover:text-red-900"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  ) : null}
                </div>
              </td>
              <td className="whitespace-nowrap px-3 py-3.5">
                {moduleConfig.utils.formatDate(String(row.reportDate).slice(0, 10))}
              </td>
              <td className="px-3 py-3.5 font-medium">{moduleConfig.utils.vesselLabel(row)}</td>
              <td className="whitespace-nowrap px-3 py-3.5">{row.reportTime || '—'}</td>
              <td className="whitespace-nowrap px-3 py-3.5">{row.condition || '—'}</td>
              <td className="max-w-[13rem] truncate px-3 py-3.5" title={formatActivities(row)}>
                {formatActivities(row)}
              </td>
              <td className="whitespace-nowrap px-3 py-3.5">{row.nextPort || '—'}</td>
              <td className="whitespace-nowrap px-3 py-3.5 text-right">
                {moduleConfig.utils.formatNumber(row.distanceCovered)}
              </td>
              <td className="whitespace-nowrap px-3 py-3.5 text-right">
                {moduleConfig.utils.formatNumber(row.distanceToGo)}
              </td>
              <td className="whitespace-nowrap px-3 py-3.5">{row.cargoType || '—'}</td>
              <td className="whitespace-nowrap px-3 py-3.5 text-right">
                {moduleConfig.utils.formatNumber(row.cargoOnBoardMt, 3)}
              </td>
              <td className="whitespace-nowrap px-3 py-3.5 text-right">
                {moduleConfig.utils.formatNumber(row.avgSpeed24h)}
              </td>
              <td className="whitespace-nowrap px-3 py-3.5 text-right">
                {moduleConfig.utils.formatNumber(row.robFoMt, 3)}
              </td>
              <td className="whitespace-nowrap px-3 py-3.5 text-right">
                {moduleConfig.utils.formatNumber(row.robMgoMdoMt, 3)}
              </td>
              <td className="whitespace-nowrap px-3 py-3.5">
                {row.latitude || row.longitude
                  ? `${row.latitude || '—'}, ${row.longitude || '—'}`
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </GeneralList>
  );
};

export default List;
